import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { navigationActions, Stacks, Tab, Tabs } from './ui/navigation';
import type { AppDispatch } from '../store';

const MAX_AGE_MS = 30 * 60 * 1000;

export type IntentRole = 'patient' | 'physician' | 'admin' | 'manager';

export interface DeepLinkIntentState {
  url: string | null;
  capturedAt: number | null;
  capturedAtRole: IntentRole | null;
}

const initialState: DeepLinkIntentState = {
  url: null,
  capturedAt: null,
  capturedAtRole: null,
};

const slice = createSlice({
  name: 'deepLinkIntent',
  initialState,
  reducers: {
    setIntent(state, action: PayloadAction<{ url: string; role: IntentRole }>) {
      state.url = action.payload.url;
      state.capturedAtRole = action.payload.role;
      state.capturedAt = Date.now();
    },
    clearIntent(state) {
      state.url = null;
      state.capturedAt = null;
      state.capturedAtRole = null;
    },
  },
});

export const deepLinkActions = slice.actions;
export default slice.reducer;

export function selectFreshIntent(
  state: { deepLinkIntent: DeepLinkIntentState },
  role: IntentRole,
): string | null {
  const { url, capturedAt, capturedAtRole } = state.deepLinkIntent;
  if (!url || !capturedAt) return null;
  if (Date.now() - capturedAt > MAX_AGE_MS) return null;
  if (capturedAtRole !== role) return null;
  return url;
}

// Routes a deep-link URL into the correct nav stack: a path whose first
// segment matches a tab (e.g. `/appointments/booking/xxx`) is pushed onto
// that tab's stack (and the tab is activated). Anything else is pushed onto
// the Master stack. Mirrors the routing in `pathnameChanged`.
export function navigateToIntent(intent: string) {
  return (dispatch: AppDispatch) => {
    const [pathname = '', query] = intent.split('?');
    const parts = pathname.split('/');
    const tabValues = Object.values(Tabs) as string[];
    const isTabRoute = !!parts[1] && tabValues.includes(parts[1]);

    if (isTabRoute) {
      const tab = parts[1] as Tab;
      const tabRoute = '/' + parts.slice(2).join('/') + (query ? `?${query}` : '');
      dispatch(navigationActions.activeTabChanged({ tab }));
      dispatch(navigationActions.pushedOntoStack({ stack: tab, route: tabRoute }));
    } else {
      dispatch(navigationActions.pushedOntoStack({ stack: Stacks.Master, route: intent }));
    }
  };
}
